import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import {
  Check,
  Car,
  FileText,
  Shield,
  ChevronRight,
  Loader,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";

const ReviewSubmitScreen = ({ userData, onComplete, onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasDimoData, setHasDimoData] = useState(false);
  const [hasSurveyData, setHasSurveyData] = useState(false);
  const [hasDocumentData, setHasDocumentData] = useState(false);

  // Check if data exists in session storage
  useEffect(() => {
    try {
      // Check if DIMO data exists
      const dimoDataString = sessionStorage.getItem("dimoData");
      if (dimoDataString) {
        const dimoData = JSON.parse(dimoDataString);
        setHasDimoData(
          dimoData && dimoData.vehicles && dimoData.vehicles.length > 0
        );
      }

      // Check if survey data exists
      const surveyDataString = sessionStorage.getItem("surveyData");
      if (surveyDataString) {
        const surveyData = JSON.parse(surveyDataString);
        setHasSurveyData(surveyData && Object.keys(surveyData).length > 0);
      }

      // Check if document data exists
      const documentDataString = sessionStorage.getItem("documentData");
      if (documentDataString) {
        setHasDocumentData(true);
      }
    } catch (err) {
      console.error("Error loading session data:", err);
      setError("Could not load your profile information. Please try again.");
    }
  }, []);

  const handleSubmit = () => {
    setIsLoading(true);
    setError(null);

    // Simulate API call to finalize profile
    setTimeout(() => {
      setIsLoading(false);

      // Store submission timestamp in session storage
      sessionStorage.setItem(
        "profileSubmitted",
        JSON.stringify({
          timestamp: new Date().toISOString(),
          profileComplete: true,
        })
      );

      // Move to success screen
      if (onComplete) {
        onComplete();
      }
    }, 1500);
  };

  return (
    <div className="space-y-6 p-4">
      <div className="text-center space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">
          Almost Done! Let's Double Check Everything
        </h1>
        <p className="text-gray-600">
          Here's what you've shared so far. Please review and submit your
          profile so we can start finding buyers and offers.
        </p>
      </div>

      {/* Status Overview Card */}
      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Vehicle Data Status */}
            <div className="flex items-center gap-3">
              {hasDimoData ? (
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" />
              ) : (
                <div className="w-6 h-6 flex-shrink-0"></div> // Empty space for alignment
              )}
              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-gray-900">
                  {hasDimoData
                    ? "Vehicle data synchronized"
                    : "Vehicle sync skipped"}
                </span>
              </div>
            </div>

            {/* Insurance Information Status */}
            <div className="flex items-center gap-3">
              {hasSurveyData ? (
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" />
              ) : (
                <div className="w-6 h-6 flex-shrink-0"></div>
              )}
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-gray-900">
                  Insurance information collected
                </span>
              </div>
            </div>

            {/* Insurance Policy Status */}
            <div className="flex items-center gap-3">
              {hasDocumentData ? (
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" />
              ) : (
                <div className="w-6 h-6 flex-shrink-0"></div>
              )}
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-gray-900">
                  Insurance policy synchronized
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Information */}
      {/* <Card className="border border-blue-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900">Your Data is Protected</p>
              <p className="text-xs text-gray-600">
                By submitting your profile, you're allowing Ownli to find relevant offers from insurance partners. 
                Your data is protected with bank-level encryption and you can revoke access at any time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card> */}

      {/* Error message if any */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p>{error}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
      >
        {isLoading ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            <span>Submitting...</span>
          </>
        ) : (
          <>
            <span>Submit</span>
            {/* <ChevronRight className="w-4 h-4" /> */}
          </>
        )}
      </button>

      <button
        onClick={onBack}
        className="w-full uppercase h-12 border-2 border-darkGrey font-bold text-darkGrey px-4 rounded-xl transition-colors !mt-4"
      >
        Back
      </button>
    </div>
  );
};

export default ReviewSubmitScreen;
